import { useUpdateEffect } from '@cian/react-utils';

import { type FC, type JSX, useCallback, useMemo, useState } from 'react';

import { useApplicationContext } from '../../../../utils/applicationContext';
import { useMortgageCalculatorContext } from '../../contexts/MortgageCalculatorContext';
import { type IPromo } from '../../types/promos';
import { calculateMonthlyPayment } from '../../utils/calculateMonthlyPayment';
import { getCianMortgagePromoUrl } from '../../utils/getCianMortgagePromoUrl';
import { FlatsError } from '../FlatsError';
import { MortgageOffersFilters } from '../MortgageOffersFilters';
import { HeaderRow, MortgageOfferRow, MortgageOffersTable, RowCountController } from '../MortgageOffersTable';
import { trackMortgageOffersTableExpandToggle } from '../MortgageOffersTable/internal/tracking/trackMortgageOffersTableExpandToggle';
import { SeoMortgageLinkContainer } from '../SeoMortgageLink';

import { CianMortgageTextContainer } from './components/CianMortgageTextContainer';
import { trackClickPromoBlock } from './internal/tracking/trackClickPromoBlock';
import { trackDownPaymentFilterChange } from './internal/tracking/trackDownPaymentFilterChange';
import { trackGetOnlineClick } from './internal/tracking/trackGetOnlineClick';
import { trackObjectCostFilterChange } from './internal/tracking/trackObjectCostFilterChange';
import { trackTermFilterChange } from './internal/tracking/trackTermFilterChange';
import { debounceTracking, filterMortgageOffers } from './internal/utils';

interface IMortgageOffersContainerProps {
  onDisplayedOffersChange: (promo: IPromo[]) => void;
}

export const MortgageOffersContainer: FC<IMortgageOffersContainerProps> = ({ onDisplayedOffersChange }) => {
  const { config } = useApplicationContext();

  const { mortgagePromos, fromDeveloperMinPrice, fromDeveloperMaxPrice, filtersDefaults } =
    useMortgageCalculatorContext();

  const getDefaultDownPayment = useCallback<() => number>(
    () => Math.round(fromDeveloperMinPrice * filtersDefaults.downPaymentRate),
    [filtersDefaults.downPaymentRate, fromDeveloperMinPrice],
  );

  const [price, setPrice] = useState<number>(fromDeveloperMinPrice);
  const [downPayment, setDownPayment] = useState<number>(getDefaultDownPayment);
  const [term, setTerm] = useState<number>(filtersDefaults.loanTerm);

  useUpdateEffect(() => {
    setPrice(fromDeveloperMinPrice);
    setDownPayment(getDefaultDownPayment());
    setTerm(filtersDefaults.loanTerm);
  }, [fromDeveloperMinPrice, fromDeveloperMaxPrice, filtersDefaults.downPaymentRate, filtersDefaults.loanTerm]);

  const filteredMortgagePromos = useMemo<IPromo[]>(
    () => filterMortgageOffers(mortgagePromos, price, downPayment, term),
    [mortgagePromos, price, downPayment, term],
  );

  const mortgagePartnerLink = getCianMortgagePromoUrl(config);

  const trackDownPaymentFilterChangeDebounced = useMemo(() => debounceTracking(trackDownPaymentFilterChange), []);
  const trackObjectCostFilterChangeDebounced = useMemo(() => debounceTracking(trackObjectCostFilterChange), []);
  const trackTermFilterChangeDebounced = useMemo(() => debounceTracking(trackTermFilterChange), []);

  const handleRowClick = useCallback<(promo: IPromo, position: number) => void>((promo, position) => {
    trackClickPromoBlock(promo, position);
  }, []);

  const handleGetOnlineClick = useCallback<(promo: IPromo) => void>(
    promo => {
      const index = filteredMortgagePromos.findIndex(
        item => item.uniquePromoId === promo.uniquePromoId && item.promoType === promo.promoType,
      );
      trackGetOnlineClick(promo, index + 1);
    },
    [filteredMortgagePromos],
  );

  const handleDownPaymentChange = useCallback<(value: number) => void>(
    value => {
      setDownPayment(value);
      trackDownPaymentFilterChangeDebounced();
    },
    [trackDownPaymentFilterChangeDebounced],
  );

  const handlePriceChange = useCallback<(value: number) => void>(
    value => {
      setPrice(value);
      trackObjectCostFilterChangeDebounced();

      const defaultDownPayment = getDefaultDownPayment();
      const minDownPayment = Math.round(filtersDefaults.minDownPayment * value);

      // Если первоначальный взнос меньше минимального, то устанавливаем минимальный
      if (downPayment < minDownPayment) {
        setDownPayment(Math.round(value * filtersDefaults.downPaymentRate));
        // Если первоначальный взнос больше максимального, то устанавливаем минимальный
      } else if (downPayment > price) {
        setDownPayment(defaultDownPayment);
      }
    },
    [
      downPayment,
      filtersDefaults.downPaymentRate,
      filtersDefaults.minDownPayment,
      getDefaultDownPayment,
      price,
      trackObjectCostFilterChangeDebounced,
    ],
  );

  const handleTermChange = useCallback<(value: number) => void>(
    value => {
      setTerm(value);
      trackTermFilterChangeDebounced();
    },
    [trackTermFilterChangeDebounced],
  );

  const handleTableExpandToggle = useCallback((expanded: boolean) => {
    trackMortgageOffersTableExpandToggle(expanded);
  }, []);

  return (
    <div data-testid="MortgageOffers">
      <MortgageOffersFilters
        downPayment={downPayment}
        filtersDefaults={filtersDefaults}
        maxPrice={fromDeveloperMaxPrice}
        price={price}
        term={term}
        onDownPaymentChanged={handleDownPaymentChange}
        onDownPaymentMouseDown={trackDownPaymentFilterChange}
        onPriceChanged={handlePriceChange}
        onPriceMouseDown={trackObjectCostFilterChange}
        onTermChanged={handleTermChange}
        onTermMouseDown={trackTermFilterChange}
      />
      <MortgageOffersTable>
        <RowCountController
          content={(promos): JSX.Element => (
            <>
              <HeaderRow />
              {promos.map((promo, index) => (
                <MortgageOfferRow
                  key={promo.uniquePromoId}
                  monthlyPayment={calculateMonthlyPayment({
                    initialPayment: downPayment,
                    interestRate: promo.mortgageParams?.interestRate,
                    loanTerm: term,
                    price,
                  })}
                  mortgagePartnerLink={mortgagePartnerLink}
                  promo={promo}
                  onClick={p => handleRowClick(p, index)}
                  onGetOnlineClick={handleGetOnlineClick}
                />
              ))}
            </>
          )}
          error={
            <FlatsError
              image="not-found"
              text={<CianMortgageTextContainer />}
              title="Нет подходящих предложений от банков и застройщиков"
            />
          }
          mortgageSeoLink={<SeoMortgageLinkContainer />}
          promos={filteredMortgagePromos}
          onDisplayedOffersChange={onDisplayedOffersChange}
          onExpandToggle={handleTableExpandToggle}
        />
      </MortgageOffersTable>
    </div>
  );
};
